/**
 * APContext — Phase 5 Accounts Payable.
 *
 * - Bills post DR <expense/WIP> / CR 2000 AP on creation through the GL.
 * - Payments post DR AP / CR Cash|CC Payable.
 * - Vendor statements imported as append-only rows for per-vendor reconciliation.
 *
 * No external service yet; OCR/email bill capture plugs in later by calling
 * `createBill` with the parsed payload — engine + posting are unchanged.
 */
import {
  createContext, useCallback, useContext, useMemo, useState,
} from "react";
import { useGeneralLedger } from "../GeneralLedgerContext";
import type {
  Bill, BillLine, BillPayment, BillPaymentMethod, BillStatus,
  VendorStatementLine,
} from "./apTypes";

const CENT = (n: number) => Math.round(n * 100);
const AP_ACCOUNT = "2000";

interface NewBillInput {
  vendorId: string;
  vendorName: string;
  invoiceNumber: string;
  billDate: string;
  dueDate: string;
  lines: Array<Omit<BillLine, "id">>;
  notes?: string;
  createdBy?: string;
}

interface RecordPaymentInput {
  billId: string;
  paidAt: string;
  amount: number;
  method: BillPaymentMethod;
  fromAccountId: string;
  reference?: string;
  recordedBy?: string;
}

export interface FifoPayPlanItem {
  billId: string;
  invoiceNumber: string;
  vendorName: string;
  billDate: string;
  dueDate: string;
  openBalance: number;
  payAmount: number;
  discountAmount: number;     // 0 if no discount applies
  totalApplied: number;       // payAmount + discountAmount; closes openBalance to zero when ===
  fullyClosed: boolean;
}

export interface FifoPayPlan {
  vendorId: string;
  items: FifoPayPlanItem[];
  cashOut: number;            // sum of payAmount
  discountTotal: number;      // sum of discountAmount
  amountApplied: number;      // sum of totalApplied
  amountRemaining: number;    // unallocated cash (input - cashOut - discountTotal)
}

interface PayVendorFifoInput {
  vendorId: string;
  amount: number;             // cash budget to spend
  paidAt: string;
  method: BillPaymentMethod;
  fromAccountId: string;
  reference?: string;
  recordedBy?: string;
  /** Optional early-pay terms: { ratePct: 2, withinDays: 10 } → 2% off if
   *  paid within 10 days of billDate. Applied per eligible bill. */
  earlyPayTerms?: { ratePct: number; withinDays: number };
  /** GL account to credit the discount to (defaults to 4990 Purchase Discounts). */
  discountAccountId?: string;
}

interface APContextValue {
  bills: Bill[];
  payments: BillPayment[];
  statementLines: VendorStatementLine[];
  getBillsForVendor: (vendorId: string) => Bill[];
  getOpenBalanceForVendor: (vendorId: string) => number;
  createBill: (input: NewBillInput) => Bill;
  recordPayment: (input: RecordPaymentInput) => BillPayment;
  /** Preview a FIFO payment plan without posting anything. */
  planVendorFifo: (input: PayVendorFifoInput) => FifoPayPlan;
  /** Execute the FIFO plan: posts one BillPayment per bill it touches. */
  payVendorFifo: (input: PayVendorFifoInput) => { payments: BillPayment[]; plan: FifoPayPlan };
  voidBill: (id: string, reason: string, by?: string) => void;
  importVendorStatement: (vendorId: string, csv: string, by?: string) =>
    { imported: number; errors: string[] };
  matchStatementLineToBill: (statementLineId: string, billId: string, by?: string) => void;
  ignoreStatementLine: (statementLineId: string, by?: string) => void;
}

const APContext = createContext<APContextValue | null>(null);

export function APProvider({ children }: { children: React.ReactNode }) {
  const gl = useGeneralLedger();
  const [bills, setBills] = useState<Bill[]>([]);
  const [payments, setPayments] = useState<BillPayment[]>([]);
  const [statementLines, setStatementLines] = useState<VendorStatementLine[]>([]);

  const getBillsForVendor = useCallback(
    (vendorId: string) => bills.filter(b => b.vendorId === vendorId),
    [bills],
  );

  const getOpenBalanceForVendor = useCallback((vendorId: string) => {
    return bills
      .filter(b => b.vendorId === vendorId && b.status !== "void" && b.status !== "paid")
      .reduce((s, b) => s + (b.total - b.amountPaid), 0);
  }, [bills]);

  const createBill = useCallback((input: NewBillInput): Bill => {
    if (input.lines.length === 0) throw new Error("Bill needs at least one line.");

    // Period-close cascade: a bill is itself a JE-posting event, so refuse
    // to create one whose posting (bill) date lands in a sealed period.
    const billPeriod = input.billDate.slice(0, 7);
    if (gl.isPeriodClosed(billPeriod)) {
      throw new Error(`Cannot create bill — period ${billPeriod} is closed. Reopen it first.`);
    }

    // Idempotency guard — refuse duplicate (vendor, invoiceNumber) pairs.
    const dup = bills.find(
      b => b.vendorId === input.vendorId &&
           b.invoiceNumber.trim().toLowerCase() === input.invoiceNumber.trim().toLowerCase() &&
           b.status !== "void",
    );
    if (dup) throw new Error(`Bill ${input.invoiceNumber} already exists for this vendor.`);

    const linesWithIds: BillLine[] = input.lines.map((l, i) => ({
      ...l,
      id: `bl_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 5)}`,
    }));
    const total = linesWithIds.reduce((s, l) => s + l.amount, 0);
    if (CENT(total) <= 0) throw new Error("Bill total must be positive.");

    // Mint bill.id BEFORE posting so the JE's sourceRef is stable & idempotency-safe.
    const billId = `bill_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`;

    // Post JE: DR each line account / CR 2000 AP
    // Memo carries an explicit "[material]" / "[labor]" tag so downstream
    // WIP-release bucketing is reliable (not memo-substring guessing).
    const je = gl.postJournal({
      postingDate: input.billDate,
      description: `AP Bill ${input.invoiceNumber} — ${input.vendorName}`,
      source: "ap_bill",
      sourceRef: { kind: "ap_bill", id: billId },
      createdBy: input.createdBy ?? "Steve",
      lines: [
        ...linesWithIds.map(l => {
          const tag = l.costType === "labor" ? "[labor] " :
            l.costType === "material" ? "[material] " : "";
          return {
            accountId: l.glAccountId,
            amount: l.amount,
            side: "debit" as const,
            memo: `${tag}${l.description}`.trim(),
            vendorId: input.vendorId,
            jobId: l.jobId,
            tradeType: l.tradeType,
          };
        }),
        {
          accountId: AP_ACCOUNT,
          amount: total,
          side: "credit" as const,
          memo: `${input.vendorName} · ${input.invoiceNumber}`,
          vendorId: input.vendorId,
        },
      ],
    });

    const now = new Date().toISOString();
    const bill: Bill = {
      id: billId,
      vendorId: input.vendorId,
      vendorName: input.vendorName,
      invoiceNumber: input.invoiceNumber,
      billDate: input.billDate,
      dueDate: input.dueDate,
      total,
      lines: linesWithIds,
      status: "open",
      amountPaid: 0,
      postedJournalEntryId: je.id,
      createdAt: now,
      createdBy: input.createdBy ?? "Steve",
      notes: input.notes,
    };
    setBills(prev => [bill, ...prev]);
    return bill;
  }, [bills, gl]);

  const recordPayment = useCallback((input: RecordPaymentInput): BillPayment => {
    const bill = bills.find(b => b.id === input.billId);
    if (!bill) throw new Error("Bill not found");
    if (bill.status === "void") throw new Error("Bill is voided");
    const open = bill.total - bill.amountPaid;
    if (CENT(input.amount) <= 0) throw new Error("Payment must be positive");
    if (CENT(input.amount) > CENT(open)) throw new Error(`Payment exceeds open balance of $${open.toFixed(2)}`);
    // Cannot pay a bill before it was issued — common data-entry error
    // that otherwise lands as a negative AP age and a broken cash-flow tie-out.
    if (input.paidAt < bill.billDate) {
      throw new Error(`Payment date (${input.paidAt}) is before bill date (${bill.billDate}).`);
    }
    // Period-close cascade: the payment posts a cash JE on `paidAt` — block
    // it if that month is sealed.
    const payPeriod = input.paidAt.slice(0, 7);
    if (gl.isPeriodClosed(payPeriod)) {
      throw new Error(`Cannot record payment — period ${payPeriod} is closed.`);
    }

    // Mint payment.id BEFORE posting so the JE's sourceRef is unique per payment.
    const paymentId = `pay_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`;

    // DR AP / CR Cash or CC Payable
    const je = gl.postJournal({
      postingDate: input.paidAt,
      description: `AP Payment — ${bill.vendorName} ${bill.invoiceNumber}`,
      source: "ap_payment",
      sourceRef: { kind: "ap_payment", id: paymentId },
      createdBy: input.recordedBy ?? "Steve",
      lines: [
        {
          accountId: AP_ACCOUNT,
          amount: input.amount,
          side: "debit",
          memo: `Pay ${bill.invoiceNumber}`,
          vendorId: bill.vendorId,
        },
        {
          accountId: input.fromAccountId,
          amount: input.amount,
          side: "credit",
          memo: `${input.method.toUpperCase()} ${input.reference ?? ""}`.trim(),
          vendorId: bill.vendorId,
        },
      ],
    });

    const now = new Date().toISOString();
    const payment: BillPayment = {
      id: paymentId,
      billId: bill.id,
      paidAt: input.paidAt,
      amount: input.amount,
      method: input.method,
      fromAccountId: input.fromAccountId,
      reference: input.reference,
      postedJournalEntryId: je.id,
      recordedBy: input.recordedBy ?? "Steve",
    };
    setPayments(prev => [payment, ...prev]);

    const newPaid = bill.amountPaid + input.amount;
    const newStatus: BillStatus =
      CENT(newPaid) >= CENT(bill.total) ? "paid" : "partially_paid";
    setBills(prev => prev.map(b =>
      b.id === bill.id ? { ...b, amountPaid: newPaid, status: newStatus } : b,
    ));
    return payment;
  }, [bills, gl]);

  // ─── FIFO bill payment with optional early-pay discount ───────────────────
  // Plan/pay against a vendor's oldest open bills first. If `earlyPayTerms`
  // is supplied, bills within `withinDays` of billDate get a `ratePct` discount
  // on the OPEN balance — discount + cash combine to close the bill. The
  // discount posts: DR 2000 AP (discount) / CR 4990 Purchase Discounts.
  const planVendorFifo = useCallback((input: PayVendorFifoInput): FifoPayPlan => {
    if (CENT(input.amount) <= 0) throw new Error("Payment amount must be positive.");
    const open = bills
      .filter(b => b.vendorId === input.vendorId &&
                   b.status !== "void" && b.status !== "paid" &&
                   CENT(b.total - b.amountPaid) > 0)
      .sort((a, b) => a.billDate.localeCompare(b.billDate) || a.invoiceNumber.localeCompare(b.invoiceNumber));

    const items: FifoPayPlanItem[] = [];
    let budgetCents = CENT(input.amount);

    const terms = input.earlyPayTerms;
    const paidAt = new Date(input.paidAt + "T00:00:00Z").getTime();

    for (const b of open) {
      if (budgetCents <= 0) break;
      const openCents = CENT(b.total - b.amountPaid);

      // Discount eligibility: only on the OPEN remainder, only if within window.
      let discountCents = 0;
      if (terms && terms.ratePct > 0) {
        const billTs = new Date(b.billDate + "T00:00:00Z").getTime();
        const daysSince = Math.floor((paidAt - billTs) / 86_400_000);
        if (daysSince >= 0 && daysSince <= terms.withinDays) {
          discountCents = Math.round(openCents * (terms.ratePct / 100));
        }
      }

      // Try to close the bill fully (cash + discount = openCents).
      const cashNeededToClose = openCents - discountCents;
      let payCents: number;
      let appliedDiscountCents: number;
      let fullyClosed: boolean;

      if (budgetCents >= cashNeededToClose) {
        payCents = cashNeededToClose;
        appliedDiscountCents = discountCents;
        fullyClosed = true;
      } else {
        // Partial: no discount (terms typically require full settlement to earn discount).
        payCents = budgetCents;
        appliedDiscountCents = 0;
        fullyClosed = false;
      }

      if (payCents <= 0 && appliedDiscountCents <= 0) continue;
      items.push({
        billId: b.id,
        invoiceNumber: b.invoiceNumber,
        vendorName: b.vendorName,
        billDate: b.billDate,
        dueDate: b.dueDate,
        openBalance: openCents / 100,
        payAmount: payCents / 100,
        discountAmount: appliedDiscountCents / 100,
        totalApplied: (payCents + appliedDiscountCents) / 100,
        fullyClosed,
      });
      budgetCents -= payCents;
    }

    const cashOut = items.reduce((s, i) => s + i.payAmount, 0);
    const discountTotal = items.reduce((s, i) => s + i.discountAmount, 0);
    return {
      vendorId: input.vendorId,
      items,
      cashOut,
      discountTotal,
      amountApplied: cashOut + discountTotal,
      amountRemaining: input.amount - cashOut,
    };
  }, [bills]);

  const payVendorFifo = useCallback((input: PayVendorFifoInput) => {
    const plan = planVendorFifo(input);
    if (plan.items.length === 0) {
      throw new Error("No open bills to pay for this vendor.");
    }

    const payPeriod = input.paidAt.slice(0, 7);
    if (gl.isPeriodClosed(payPeriod)) {
      throw new Error(`Cannot record payment — period ${payPeriod} is closed.`);
    }

    const discountAccountId = input.discountAccountId ?? "4990";
    const posted: BillPayment[] = [];

    for (const item of plan.items) {
      // 1) Cash payment (uses existing recordPayment for invariants + status updates).
      if (CENT(item.payAmount) > 0) {
        const p = recordPayment({
          billId: item.billId,
          paidAt: input.paidAt,
          amount: item.payAmount,
          method: input.method,
          fromAccountId: input.fromAccountId,
          reference: input.reference
            ? `${input.reference} · FIFO`
            : `FIFO pay · ${item.invoiceNumber}`,
          recordedBy: input.recordedBy,
        });
        posted.push(p);
      }

      // 2) Discount JE: DR 2000 AP / CR 4990 Purchase Discounts.
      // We post directly to the GL (not via recordPayment) so it doesn't
      // double-count as cash, but we still bump amountPaid on the bill so
      // status flips to "paid".
      if (CENT(item.discountAmount) > 0) {
        const bill = bills.find(b => b.id === item.billId);
        if (!bill) continue;
        const disc = gl.postJournal({
          postingDate: input.paidAt,
          description: `Early-pay discount — ${bill.vendorName} ${bill.invoiceNumber}`,
          source: "ap_payment",
          sourceRef: { kind: "ap_discount", id: `${item.billId}_${input.paidAt}` },
          createdBy: input.recordedBy ?? "Steve",
          lines: [
            {
              accountId: AP_ACCOUNT,
              amount: item.discountAmount,
              side: "debit",
              memo: `Early-pay discount ${bill.invoiceNumber}`,
              vendorId: bill.vendorId,
            },
            {
              accountId: discountAccountId,
              amount: item.discountAmount,
              side: "credit",
              memo: `2/10 net 30 discount captured`,
            },
          ],
        });
        // Update bill: bump amountPaid so status reflects closure, and record
        // a synthetic payment row tagged as discount for vendor history.
        const newPaid = bill.amountPaid + item.payAmount + item.discountAmount;
        const newStatus: BillStatus =
          CENT(newPaid) >= CENT(bill.total) ? "paid" : "partially_paid";
        setBills(prev => prev.map(b =>
          b.id === bill.id ? { ...b, amountPaid: newPaid, status: newStatus } : b,
        ));
        const discPayment: BillPayment = {
          id: `pay_disc_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
          billId: bill.id,
          paidAt: input.paidAt,
          amount: item.discountAmount,
          method: input.method,
          fromAccountId: discountAccountId, // marks this row as a discount, not real cash
          reference: `Discount · ${input.reference ?? ""}`.trim(),
          postedJournalEntryId: disc.id,
          recordedBy: input.recordedBy ?? "Steve",
        };
        setPayments(prev => [discPayment, ...prev]);
        posted.push(discPayment);
      }
    }

    return { payments: posted, plan };
  }, [planVendorFifo, recordPayment, gl, bills]);


  const voidBill = useCallback((id: string, reason: string, by = "Steve") => {
    const bill = bills.find(b => b.id === id);
    if (!bill) throw new Error("Bill not found");
    if (bill.status === "void") return;

    // Reverse every payment against this bill BEFORE reversing the bill itself,
    // otherwise AP control account goes negative for the vendor.
    const now = new Date().toISOString();
    const relatedPayments = payments.filter(p => p.billId === id);
    const voidedPaymentIds: string[] = [];
    for (const p of relatedPayments) {
      try {
        gl.reverseJournal(p.postedJournalEntryId, `Void bill ${bill.invoiceNumber} — reverse payment ${p.id}`, by);
        voidedPaymentIds.push(p.id);
      } catch {
        // tolerate already-reversed payment JEs (idempotent void)
      }
    }
    gl.reverseJournal(bill.postedJournalEntryId, `Void bill ${bill.invoiceNumber}: ${reason}`, by);
    // Mark payment rows voided so cash-flow / vendor history reflect the reversal.
    if (voidedPaymentIds.length) {
      setPayments(prev => prev.map(p =>
        voidedPaymentIds.includes(p.id)
          ? { ...p, voidedAt: now, voidedBy: by, voidReason: `Bill voided: ${reason}` }
          : p,
      ));
    }
    setBills(prev => prev.map(b =>
      b.id === id
        ? { ...b, status: "void", voidedAt: now, voidedBy: by, voidReason: reason, amountPaid: 0 }
        : b,
    ));
  }, [bills, payments, gl]);

  const importVendorStatement = useCallback(
    (vendorId: string, csv: string, by = "Steve") => {
      const errors: string[] = [];
      const lines = csv.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      const hasHeader = /date/i.test(lines[0] ?? "") && /amount/i.test(lines[0] ?? "");
      const data = hasHeader ? lines.slice(1) : lines;
      const now = new Date().toISOString();
      const rows: VendorStatementLine[] = [];
      data.forEach((line, idx) => {
        const cols = line.split(",").map(c => c.trim().replace(/^"|"$/g, ""));
        if (cols.length < 3) {
          errors.push(`Row ${idx + 1}: needs date,description,amount`);
          return;
        }
        const [date, description, amountStr, reference] = cols;
        const amt = parseFloat(amountStr.replace(/[$,]/g, ""));
        if (!date || Number.isNaN(amt)) {
          errors.push(`Row ${idx + 1}: invalid date/amount`);
          return;
        }
        rows.push({
          id: `vsl_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 5)}`,
          vendorId,
          postedDate: date,
          description,
          amount: Math.abs(amt),
          reference: reference || undefined,
          status: "unmatched",
          importedAt: now,
          importedBy: by,
        });
      });
      if (rows.length) setStatementLines(prev => [...rows, ...prev]);
      return { imported: rows.length, errors };
    },
    [],
  );

  const matchStatementLineToBill = useCallback(
    (statementLineId: string, billId: string, by = "Steve") => {
      const line = statementLines.find(l => l.id === statementLineId);
      const bill = bills.find(b => b.id === billId);
      if (!line || !bill) throw new Error("Not found");
      setStatementLines(prev => prev.map(l =>
        l.id === statementLineId
          ? { ...l, status: "matched", matchedBillId: billId,
              matchedAt: new Date().toISOString(), matchedBy: by }
          : l,
      ));
    },
    [statementLines, bills],
  );

  const ignoreStatementLine = useCallback((statementLineId: string, by = "Steve") => {
    setStatementLines(prev => prev.map(l =>
      l.id === statementLineId
        ? { ...l, status: "ignored", matchedAt: new Date().toISOString(), matchedBy: by }
        : l,
    ));
  }, []);

  const value = useMemo<APContextValue>(() => ({
    bills, payments, statementLines,
    getBillsForVendor, getOpenBalanceForVendor,
    createBill, recordPayment, planVendorFifo, payVendorFifo, voidBill,
    importVendorStatement, matchStatementLineToBill, ignoreStatementLine,
  }), [bills, payments, statementLines, getBillsForVendor, getOpenBalanceForVendor,
       createBill, recordPayment, planVendorFifo, payVendorFifo, voidBill, importVendorStatement,
       matchStatementLineToBill, ignoreStatementLine]);

  return <APContext.Provider value={value}>{children}</APContext.Provider>;
}

export function useAP() {
  const ctx = useContext(APContext);
  if (!ctx) throw new Error("useAP must be used inside <APProvider>");
  return ctx;
}
